import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { environment } from '@ion/env';
import { Observable, tap } from 'rxjs';

import { PermissionCheckOptions } from './auth.model';
import { AuthorizationService } from './authorization.service';

interface AuthorizationGuardOptionsBase {
  permissionCheckOptions?: PermissionCheckOptions;
}
interface AuthorizationGuardOptionsUserHasAllPermissions extends AuthorizationGuardOptionsBase {
  userHasAllPermissions: string | string[];
}
interface AuthorizationGuardOptionsUserHasAnyPermission extends AuthorizationGuardOptionsBase {
  userHasAnyPermission: string | string[];
}
export type AuthorizationGuardOptions =
  AuthorizationGuardOptionsUserHasAllPermissions | AuthorizationGuardOptionsUserHasAnyPermission;

/**
 * The authorizationGuard checks if the user has the given permissions.
 * If the user isn't authorized, a redirect will happen to the 403 page.
 */
export function authorizationGuard(options: AuthorizationGuardOptions): CanActivateFn {
  return (route: ActivatedRouteSnapshot): Observable<boolean> => {
    const authorizationService = inject(AuthorizationService);
    const router = inject(Router);

    const isAuthorized = isAuthorizationGuardOptionsUserHasAllPermissions(options)
      ? authorizationService.userHasAllPermissions(options.userHasAllPermissions, options.permissionCheckOptions)
      : authorizationService.userHasAnyPermission(options.userHasAnyPermission, options.permissionCheckOptions);

    return isAuthorized.pipe(
      tap(authorized => {
        if (!authorized) {
          if (!environment.production) {
            console.warn(
              `User is not authorized to access path ${getConfiguredPath(route)} with failed permissions:`,
              options,
            );
          }
          router.navigate(['/403']);
        }
      }),
    );
  };
}

function isAuthorizationGuardOptionsUserHasAllPermissions(
  val: AuthorizationGuardOptions,
): val is AuthorizationGuardOptionsUserHasAllPermissions {
  const allKey: keyof AuthorizationGuardOptionsUserHasAllPermissions = 'userHasAllPermissions';
  return allKey in val;
}

function getConfiguredPath(route: ActivatedRouteSnapshot): string {
  return (
    '/' +
    route.pathFromRoot
      .map(r => r.routeConfig?.path ?? '')
      .filter(segment => segment !== '')
      .join('/')
  );
}
